import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/Api';
import { customStorage } from '../services/CommonService';
import './GlobalAnnouncement.scss';

const CACHE_KEY = 'pwf-current-announcement';

const readCached = () => {
    try { return JSON.parse(customStorage.persistent.getItem(CACHE_KEY) || 'null'); }
    catch (_) { return null; }
};

const pad = value => String(value).padStart(2, '0');
const formatLocal = value => {
    const date = new Date(value);
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const maintenanceMessage = announcement => {
    const start = formatLocal(announcement.startAtUtc);
    const end = formatLocal(announcement.endAtUtc);
    const now = Date.now();
    if (now >= Date.parse(announcement.endAtUtc))
        return `Server maintenance is taking longer than planned. The application may remain unavailable. Planned time: ${start} to ${end}.`;
    if (now >= Date.parse(announcement.startAtUtc))
        return `Server maintenance is in progress until ${end}. The application is unavailable during this period.`;
    return `Server maintenance is scheduled from ${start} to ${end}. The application will be unavailable during this period.`;
};

export default function GlobalAnnouncement() {
    const [announcement, setAnnouncement] = useState(readCached);
    const load = useCallback(async () => {
        try {
            const value = (await api.get('/api/announcements/current', { _anonymous: true })).data;
            setAnnouncement(value || null);
            if (value) customStorage.persistent.setItem(CACHE_KEY, JSON.stringify(value));
            else customStorage.persistent.removeItem(CACHE_KEY);
        } catch (_) { /* retain the cached maintenance warning while the API is offline */ }
    }, []);
    useEffect(() => {
        void load();
        const interval = setInterval(load, 30000);
        const visible = () => { if (document.visibilityState === 'visible') void load(); };
        document.addEventListener('visibilitychange', visible);
        return () => { clearInterval(interval); document.removeEventListener('visibilitychange', visible); };
    }, [load]);
    useEffect(() => {
        document.documentElement.classList.toggle('has-global-announcement', !!announcement);
        return () => document.documentElement.classList.remove('has-global-announcement');
    }, [announcement]);
    const message = useMemo(() => !announcement ? '' : announcement.type === 'Maintenance'
        ? maintenanceMessage(announcement) : announcement.message || '', [announcement]);
    if (!announcement || !message) return null;
    return <div className="global-announcement" role="status" aria-live="polite">
        <div key={`${announcement.id}-${announcement.updatedAtUtc}`} className="global-announcement-text">{message}</div>
    </div>;
}
